import { isBrowser } from '@atomixdesign/rem-scaling'
import { Container, Grid } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import jump from 'jump.js'
import dynamic from 'next/dynamic'
import React, { PropsWithChildren, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { LazyComponent } from '../../components/lazyComponent/lazyComponent'
import { nonNull } from '../../utils/nonNull'
import { FAQBanner, PopularQuestions } from './fragments'
import { PopularQuestionItem } from './fragments/popularQuestions'
import {
  StyledFAQBannerWrapper,
  StyledFAQContentWrapper,
  StyledPopularQuestionsWrapper,
  StyledPopularQuestionTitle,
} from './styledFaqs'

import { FaqPageQuery } from '@gql'

export type FaqProps = FaqPageQuery  & {
  //
}

const TopContent = dynamic(() => import('./fragments/topContent').then(mod => mod.TopContent))
const BelowTheFold = dynamic(() => import('./fragments/belowTheFold').then(mod => mod.BelowTheFold))

// Number of Popular Questions to show when the FAQ landing page has no curated
// block to define the section size.
const DEFAULT_POPULAR_COUNT = 6

const isFaqEntry = <T extends { __typename?: string }>(
  entry: T,
): entry is Extract<T, { __typename?: 'faqs_default_Entry' }> => entry.__typename === 'faqs_default_Entry'

type RankableFaq = PopularQuestionItem & { viewCount?: number | null }

/**
 * Hybrid "Fill": lead with the most-viewed FAQs, then top up any remaining
 * slots from the editor-curated block so the section is never empty while view
 * data is still accumulating. Pure so it can run for both the static (SSR)
 * counts and the live counts fetched on the client.
 */
const buildPopularFaqs = (faqs: RankableFaq[], curated: PopularQuestionItem[]): PopularQuestionItem[] => {
  const size = curated.length > 0 ? curated.length : DEFAULT_POPULAR_COUNT

  const result: PopularQuestionItem[] = faqs
    .filter((f) => (f.viewCount ?? 0) > 0)
    .sort((a, b) => (b.viewCount ?? 0) - (a.viewCount ?? 0) || (a.title ?? '').localeCompare(b.title ?? ''))
    .slice(0, size)
    .map((f) => ({ id: f.id, title: f.title, faqText: f.faqText }))

  if (result.length < size) {
    const seen = new Set(result.map((f) => f.id))

    for (const faq of curated) {
      if (result.length >= size) break

      if (!seen.has(faq.id)) {
        result.push(faq)
        seen.add(faq.id)
      }
    }
  }

  return result
}

export const FAQ: React.FC<PropsWithChildren<FaqProps>> = (props) => {
  const { entry, entries } = props

  if (!entry || entry.__typename !== 'faqLandingPage_faqLandingPage_Entry') {
    return null
  }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const { faq, category } = useMemo(() => {
    let faq: string | undefined
    let category: string | undefined

    if (isBrowser()) {
      const slug = window.location.hash.slice(1)

      if (slug && entries) {
        const match = nonNull(entries).find((e) => e.__typename === 'faqs_default_Entry' && e.slug === slug)

        if (match && match.__typename == 'faqs_default_Entry') {
          faq = match.id ?? undefined
          category = match.faqCategoryType?.[0]?.id ?? undefined
        }
      }
    }

    return { faq, category }
  }, [entries])

  // FAQ items with their build-time view counts, and the editor-curated block.
  const faqEntries = useMemo<RankableFaq[]>(() => (entries ? nonNull(entries) : [])
    .filter(isFaqEntry)
    .map((e) => ({ id: e.id, title: e.title, faqText: e.faqText, viewCount: e.viewCount })), [entries])

  const curated = useMemo<PopularQuestionItem[]>(() => {
    const block = entry.popularQuestions?.[0]

    return block
      ? [...nonNull(block.leftColumnFaqs ?? []), ...nonNull(block.rightColumnFaqs ?? [])]
        .filter(isFaqEntry)
        .map((f) => ({ id: f.id, title: f.title, faqText: f.faqText }))
      : []
  }, [entry])

  // View counts are live-changing data, but the page is statically generated, so
  // the build-time order goes stale. Fetch current counts on the client and
  // re-rank. The initial (SSR) render uses the build-time counts to avoid a
  // layout shift / hydration mismatch; the live counts refine it after mount.
  const [liveCounts, setLiveCounts] = useState<Record<string, number> | null>(null)

  useEffect(() => {
    if (!isBrowser()) {
      return
    }

    let active = true

    fetch('/api/faq-popularity')
      .then((res) => (res.ok ? res.json() : null))
      .then((data: unknown) => {
        if (!active || !data || typeof data !== 'object') {
          return
        }

        // The endpoint returns a { "<faqId>": <viewCount> } map.
        const counts: Record<string, number> = {}

        for (const [id, viewCount] of Object.entries(data as Record<string, unknown>)) {
          counts[id] = Number(viewCount) || 0
        }

        setLiveCounts(counts)
      })
      .catch(() => {
        // Keep the build-time order if the live fetch fails.
      })

    return () => {
      active = false
    }
  }, [])

  const popularFaqs = useMemo<PopularQuestionItem[]>(() => {
    const faqs = liveCounts
      ? faqEntries.map((f) => ({ ...f, viewCount: f.id != null ? liveCounts[f.id] ?? 0 : f.viewCount }))
      : faqEntries

    return buildPopularFaqs(faqs, curated)
  }, [faqEntries, curated, liveCounts])

  useLayoutEffect(() => {
    setTimeout(() => {
      if (isBrowser() && faq) {
        const element = document.querySelector(window.location.hash)

        if (element) {
          const heading = element.querySelector('[role="button"]') as HTMLElement

          if (heading) {
            heading.click()
          } else {
            jump(element)
          }
        }
      }
    }, 200)
  }, [faq])

  return (
    <>
      {entry.secondaryPageBanner && (
        <StyledFAQBannerWrapper container direction={'column'}>
          <FAQBanner
            secondaryPageBanner={nonNull(entry.secondaryPageBanner)}
            layout={isMobile ? 'vertical' : 'horizontal'}
          />
        </StyledFAQBannerWrapper>
      )}
      <StyledFAQContentWrapper maxWidth={false}>
        <Container disableGutters={true}>
          <Grid direction={'column'} alignItems={'center'} container>
            {popularFaqs.length > 0 && (
              <StyledPopularQuestionsWrapper container direction={'column'} alignItems={'center'}>
                <StyledPopularQuestionTitle variant="h2" tabIndex={0}>
                  Popular Questions
                </StyledPopularQuestionTitle>
                <PopularQuestions faqs={popularFaqs}/>
              </StyledPopularQuestionsWrapper>
            )}
            <LazyComponent>
              <TopContent { ...props } category={category} />
            </LazyComponent>
          </Grid>
        </Container>
      </StyledFAQContentWrapper>
      <LazyComponent>
        <BelowTheFold { ...props } />
      </LazyComponent>
    </>
  )
}


